import React, { useState, useEffect, useMemo } from "react";
import { Button, Input, Modal, Select, Space, Tag, TreeSelect, Typography, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  customerAPIWithErrorHandling,
  customerHelpers,
} from "../../../api/customer/customer";
import { useNavigate } from "react-router-dom";
import { downloadBlob } from "../../../utils/excel";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import { groupCustomerAPIWithErrorHandling } from "../../../api/groupCustomer/groupCustomer";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmAction, confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import CustomerFormDrawer from "./CustomerFormDrawer";

// const DEFAULT_VISIBLE_FILTERS = ["search", "organization", "group"];
const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["phone", "identity_account", "organizations", "group_customer", "status"];

export default function CustomerTable() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    from: 0,
    to: 0,
    lastPage: 1,
  });
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    sort_by: "created_at",
    sort_order: "desc",
  });

  const [allGroups, setAllGroups] = useState([]);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [isExporting, setIsExporting] = useState(false);

  const [treeData, setTreeData] = useState([]);
  const [valueOrg, setValueOrg] = useState();

  // Tích chọn để xóa hàng loạt
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Thêm mới / chỉnh sửa làm bằng Drawer ngay trên danh sách.
  // Xem chi tiết vẫn là trang riêng vì màn đó có tài sản, đợt thu, thống kê.
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const flattenTreeData = (nodes, parentId = 0) => {
    let result = [];
    nodes.forEach((node) => {
      result.push({
        id: node.id,
        pId: parentId,
        value: String(node.id),
        title: node.name,
        isLeaf: node.children_recursive?.length === 0,
      });

      if (node.children_recursive && node.children_recursive.length > 0) {
        result = result.concat(
          flattenTreeData(node.children_recursive, node.id)
        );
      }
    });
    return result;
  };

  // Load Organizations for select options
  const loadOrganizations = async () => {
    try {
      const response =
        await organizationAPIWithErrorHandling.getTreeStructure();
      if (response.success) {
        const rawData = response.data;
        const flatTree = flattenTreeData(rawData);
        setTreeData(flatTree); // Dùng cho TreeSelect
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  // Nạp toàn bộ nhóm trong phạm vi quyền một lần.
  // Trước đây chỉ nạp theo đơn vị nên ô lọc nhóm bị khoá cho tới khi chọn đơn vị.
  const fetchGroupCustomer = async () => {
    try {
      setLoadingGroup(true);
      const response = await groupCustomerAPIWithErrorHandling.getGroupAll();
      setAllGroups(response?.data || []);
    } catch (error) {
      message.error("Không thể tải danh sách nhóm khách hàng: " + error.message);
    } finally {
      setLoadingGroup(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
    fetchGroupCustomer();
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chọn đơn vị thì thu hẹp danh sách nhóm cho đúng đơn vị đó,
  // chưa chọn thì hiện toàn bộ nhóm người dùng được phép thấy.
  const availableGroups = useMemo(() => {
    if (!valueOrg) return allGroups;
    return allGroups.filter(
      (group) => String(group.organization_id) === String(valueOrg)
    );
  }, [allGroups, valueOrg]);

  const onChangeOrg = (newValue) => {
    setValueOrg(newValue);

    // Nhóm đang chọn có thể không thuộc đơn vị mới -> chỉ bỏ chọn khi thật sự không còn hợp lệ
    const stillValid =
      !newValue ||
      allGroups.some(
        (group) =>
          group.id === selectedGroup &&
          String(group.organization_id) === String(newValue)
      );
    const nextGroup = stillValid ? selectedGroup : null;

    if (!stillValid) setSelectedGroup(null);
    fetchCustomers({ orgId: newValue, groupId: nextGroup });
  };

  // Xử lý khi chọn nhóm khách hàng
  const handleGroupChange = (value) => {
    // Select allowClear trả về undefined khi xoá -> ép về null để không rơi vào giá trị mặc định
    const groupId = value ?? null;
    setSelectedGroup(groupId);
    fetchCustomers({ groupId });
  };

  // Fetch customers data using the proper API.
  //
  // Nhận object thay vì tham số vị trí: bản cũ có 6 tham số vị trí nên hầu hết nơi gọi
  // truyền nhầm `status` vào đúng ô của `selectedGroup`, khiến bộ lọc nhóm bị mất
  // (hoặc bị gửi "active"/"inactive" làm groupId → truy vấn ra 0 dòng).
  // Giờ nơi gọi chỉ truyền đúng thứ vừa thay đổi, phần còn lại lấy từ state.
  const fetchCustomers = async ({
    page = 1,
    perPage = pagination.pageSize,
    search = searchText,
    orgId = valueOrg,
    groupId = selectedGroup,
    status = filters.status,
    sortBy = filters.sort_by,
    sortOrder = filters.sort_order,
  } = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: (search || "").trim(),
        orgId: orgId || "",
        groupId: groupId || "",
        status,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      const response = await customerAPIWithErrorHandling.getCustomers(params);
      const { customers: customerData, pagination: paginationData } =
        customerHelpers.extractCustomerList(response);

      setCustomers(customerData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
        from: paginationData.from,
        to: paginationData.to,
        lastPage: paginationData.lastPage,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách khách hàng");
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle table pagination change
  const handleTableChange = (paginationConfig, tableFilters, sorter) => {
    // Sắp xếp: tính tại chỗ rồi truyền thẳng, vì setFilters là bất đồng bộ nên
    // đọc lại filters.sort_by ngay sau đó sẽ luôn chậm một nhịp.
    const sortBy = sorter?.field || filters.sort_by;
    const sortOrder = sorter?.field
      ? (sorter.order === "descend" ? "desc" : "asc")
      : filters.sort_order;

    // Handle status filter
    let statusFilter = "";
    if (tableFilters && tableFilters.status && tableFilters.status.length > 0) {
      statusFilter = tableFilters.status[0] ? "active" : "inactive";
    }

    setFilters((prev) => ({
      ...prev,
      status: statusFilter,
      sort_by: sortBy,
      sort_order: sortOrder,
    }));

    fetchCustomers({
      page: paginationConfig.current,
      perPage: paginationConfig.pageSize,
      status: statusFilter,
      sortBy,
      sortOrder,
    });
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
    fetchCustomers({ search: value });
  };

  // Xoá toàn bộ bộ lọc
  const handleReset = () => {
    setSearchText("");
    setValueOrg(undefined);
    setSelectedGroup(null);
    setFilters((prev) => ({ ...prev, status: "" }));
    fetchCustomers({ search: "", orgId: "", groupId: null, status: "" });
  };

  // Action handlers
  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingRecord(null);
  };

  const handleInfo = (record) => {
    navigate(`/customers/information/${record.id}`);
  };

  // Lý do bị chặn xóa kèm cả tên đợt thu nên dài, message dòng đơn sẽ cắt cụt
  const showBlockedReason = (reason, fallback) => {
    const content = reason || fallback;
    if (content.length > 80) {
      Modal.error({ title: fallback, content });
      return;
    }
    message.error(content);
  };

  const handleDelete = async (record) => {
    try {
      const confirmed = await confirmDelete({ entity: "khách hàng", name: record.name });
      if (!confirmed) return;

      await customerAPIWithErrorHandling.deleteCustomer(record.id);
      message.success("Xóa khách hàng thành công");

      fetchCustomers({ page: pagination.current });
    } catch (error) {
      showBlockedReason(error.message, "Không thể xóa khách hàng");
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedRowKeys.length;
    if (count === 0) return;

    const confirmed = await confirmAction({
      title: `Xóa ${count} khách hàng đã chọn?`,
      content: "Khách hàng đã phát sinh khoản thu sẽ không xóa được. Tài sản đứng tên khách hàng sẽ được xóa theo.",
      okText: "Xóa",
      okType: "danger",
    });
    if (!confirmed) return;

    setBulkDeleting(true);
    try {
      const res = await customerAPIWithErrorHandling.bulkDeleteCustomers(selectedRowKeys);
      const result = res?.data?.data;

      if (result?.failed > 0) {
        // Nếu các bản ghi cùng một lý do thì nói thẳng lý do, đỡ phải xóa lẻ để dò
        const reasons = [...new Set((result.errors || []).map((e) => e.message))];
        Modal.warning({
          title: `Đã xóa ${result.success}/${count} khách hàng`,
          content:
            reasons.length === 1
              ? reasons[0]
              : `${result.failed} khách hàng không xóa được:\n\n${reasons.join("\n")}`,
          width: 560,
        });
      } else {
        message.success(res?.data?.message || `Đã xóa ${count} khách hàng`);
      }

      setSelectedRowKeys([]);

      // Xóa hết bản ghi của trang hiện tại thì lùi về trang trước
      const removed = result?.success ?? count;
      const isPageEmptied = customers.length === removed && pagination.current > 1;
      fetchCustomers({ page: isPageEmptied ? pagination.current - 1 : pagination.current });
    } catch (error) {
      showBlockedReason(error.message, "Không thể xóa các khách hàng đã chọn");
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setFormOpen(true);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = {
        ...(searchText && { search: searchText }),
        ...(valueOrg && { orgId: valueOrg }),
      };

      const response = await customerAPIWithErrorHandling.exportCustomers(params);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const currentDate = new Date().toISOString().split("T")[0].replace(/-/g, "_");
      const filename = `KHACH_HANG_${currentDate}.xlsx`;

      downloadBlob(blob, filename);
      message.success("Tải xuống danh sách khách hàng thành công!");
    } catch (error) {
      message.error(error.message || "Không thể tải xuống danh sách khách hàng");
    } finally {
      setIsExporting(false);
    }
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "name", label: "Họ tên", fixed: true,
      column: { title: "Họ tên", dataIndex: "name", key: "name", width: 200, fixed: "left", sorter: true },
    },
    {
      key: "phone", label: "Điện thoại",
      column: { title: "Điện thoại", dataIndex: "phone", key: "phone", width: 120 },
    },
    {
      key: "identity_account", label: "Mã định danh",
      column: {
        title: "Mã định danh", dataIndex: "identity_account", key: "identity_account",
        width: 120, align: "center",
        render: (value) => <Tag>{value ?? "_"}</Tag>,
      },
    },
    {
      key: "organizations", label: "Đơn vị",
      column: {
        title: "Đơn vị", dataIndex: "organizations", key: "organizations", width: 250,
        render: (organizations = []) => (
          <>
            {organizations.length > 0 ? (
              organizations.map((item) => (
                <Tag color="geekblue" key={item.id}>{item.name}</Tag>
              ))
            ) : (
              <Tag color="default">Chưa phân nhóm</Tag>
            )}
          </>
        ),
      },
    },
    {
      key: "group_customer", label: "Nhóm khách hàng",
      column: {
        title: "Nhóm khách hàng", dataIndex: "group_customer", key: "group_customer", width: 250,
        render: (groups = []) => (
          <>
            {groups.length > 0 ? (
              groups.map((item) => (
                <Tag color="blue" key={item.id}>{item.name}</Tag>
              ))
            ) : (
              <Tag color="default">Chưa phân nhóm</Tag>
            )}
          </>
        ),
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 130,
        render: (status) => (
          <Tag color={status ? "green" : "red"}>
            {status ? "Hoạt động" : "Không hoạt động"}
          </Tag>
        ),
        filters: [
          { text: "Hoạt động", value: true },
          { text: "Không hoạt động", value: false },
        ],
        filterMultiple: false,
      },
    },
    actionColumnDef((record) => [
      { key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view", onClick: () => handleInfo(record) },
      { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => handleEdit(record) },
      { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
    ]),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm theo tên, email, số điện thoại..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "organization", label: "Đơn vị", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <TreeSelect
          treeDataSimpleMode
          style={{ width: "100%" }}
          value={valueOrg}
          styles={{ popup: { root: { maxHeight: 400, overflow: "auto" } } }}
          placeholder="Chọn đơn vị"
          onChange={onChangeOrg}
          treeData={treeData}
          allowClear
        />
      ),
    },
    {
      key: "group", label: "Nhóm khách hàng", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Lọc theo nhóm"
          value={selectedGroup}
          onChange={handleGroupChange}
          disabled={loadingGroup || availableGroups.length === 0}
          loading={loadingGroup}
          notFoundContent={
            valueOrg ? "Đơn vị này chưa có nhóm khách hàng" : "Chưa có nhóm khách hàng"
          }
          allowClear
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={availableGroups.map((group) => ({ value: group.id, label: group.name }))}
        />
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || valueOrg || selectedGroup);

  return (
    <BrandedPageLayout
      fitHeight
      title="Khách hàng"
      subtitle="Quản lý danh sách khách hàng, phân nhóm và đơn vị."
      breadcrumbItems={[{ title: "Khách hàng" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => fetchCustomers({ page: pagination.current })}
        extra={
          <Space size="small">
            {selectedRowKeys.length > 0 && (
              <>
                <Typography.Text type="secondary">Đã chọn {selectedRowKeys.length}</Typography.Text>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={bulkDeleting}
                  onClick={handleBulkDelete}
                >
                  Xóa đã chọn
                </Button>
              </>
            )}
            <Button icon={<DownloadOutlined />} onClick={handleExport} loading={isExporting}>
              Xuất Excel
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Thêm mới
            </Button>
          </Space>
        }
      />

      <ListTableCard
        fitHeight
        columns={columns}
        dataSource={customers}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có khách hàng"
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: false,
          // Cột "Họ tên" đã fixed left nên cột tích chọn phải fixed theo,
          // không thì cuộn ngang là checkbox trôi ra khỏi khối đứng yên.
          fixed: true,
        }}
      />

      <CustomerFormDrawer
        open={formOpen}
        record={editingRecord}
        treeData={treeData}
        onClose={handleCloseForm}
        onSaved={() =>
          // Sửa thì ở nguyên trang đang xem, thêm mới thì về trang 1 để thấy bản ghi vừa tạo
          fetchCustomers({ page: editingRecord ? pagination.current : 1 })
        }
      />
    </BrandedPageLayout>
  );
}
